import jsQR from 'jsqr';

// 最後に使ったカメラをブラウザ単位で覚えておく (端末を変えても引きずらない)。
const STORAGE_KEY = 'nc.camera.deviceId';

export function rememberedDeviceId(): string | undefined {
    try {
        return localStorage.getItem(STORAGE_KEY) || undefined;
    } catch {
        return undefined;
    }
}

export function rememberDeviceId(deviceId: string | undefined): void {
    if (!deviceId) return;
    try {
        localStorage.setItem(STORAGE_KEY, deviceId);
    } catch {
        // プライベートブラウズ等で保存できない場合は記憶しない。
    }
}

/**
 * getUserMedia が使える環境かどうか。HTTPS (または localhost) でないと
 * ブラウザがカメラを許可しないため、その場合はボタン自体を出さない。
 */
export function cameraAvailable(): boolean {
    return !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
}

export class QrCamera {
    private stream: MediaStream | null = null;
    private generation = 0;
    private timer: ReturnType<typeof setTimeout> | undefined;
    private canvas = document.createElement('canvas');

    constructor(private video: HTMLVideoElement, private cameras: HTMLSelectElement,
        private stopButton: HTMLButtonElement, private scanButton: HTMLButtonElement,
        private message: HTMLElement, private onCode: (code: string) => void) {}

    stop() {
        ++this.generation;
        clearTimeout(this.timer);
        this.stream?.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.video.pause(); this.video.srcObject = null; this.video.hidden = true;
        this.stopButton.hidden = true; this.cameras.hidden = true; this.scanButton.disabled = false;
    }

    async start(deviceId?: string, retryWithoutDevice = true) {
        this.stop();
        const generation = this.generation;
        // 明示指定がなければ前回選択したカメラを使う。
        const requested = deviceId ?? rememberedDeviceId();
        if (!navigator.mediaDevices?.getUserMedia) {
            this.message.textContent = 'カメラを利用できません。HTTPSで開き、Safari・Chrome・Edgeなどのブラウザーからお試しください。';
            return;
        }
        this.scanButton.disabled = true; this.stopButton.hidden = false;
        this.message.textContent = 'カメラの利用を許可してください。';
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: {
                ...(requested ? {deviceId: {exact: requested}} : {facingMode: {ideal: 'environment'}}),
                width: {ideal: 1280}, height: {ideal: 720}
            }});
            // A late permission response must not reopen a cancelled camera.
            if (generation !== this.generation) { stream.getTracks().forEach(t => t.stop()); return; }
            this.stream = stream;
            rememberDeviceId(stream.getVideoTracks()[0]?.getSettings().deviceId || requested);
            this.video.muted = true; this.video.playsInline = true; this.video.srcObject = stream; this.video.hidden = false;
            await this.video.play();
            if (generation !== this.generation) return;
            this.message.textContent = 'QR全体が映るようにカメラを向けてください。';
            stream.getVideoTracks()[0]?.addEventListener('ended', () => {
                if (generation === this.generation) { this.stop(); this.message.textContent = 'カメラが停止しました。もう一度読み取ってください。'; }
            });
            // Camera labels become available after permission is granted. Enumeration failure does not stop scanning.
            navigator.mediaDevices.enumerateDevices().then(devices => {
                if (generation !== this.generation) return;
                const cameras = devices.filter(d => d.kind === 'videoinput');
                this.cameras.replaceChildren(...cameras.map((d, i) => new Option(d.label || `カメラ ${i + 1}`, d.deviceId)));
                this.cameras.value = stream.getVideoTracks()[0]?.getSettings().deviceId || requested || cameras[0]?.deviceId || '';
                this.cameras.hidden = cameras.length < 2;
            }).catch(() => {});
            const context = this.canvas.getContext('2d', {willReadFrequently: true});
            if (!context) throw new Error('Canvas unavailable');
            const detect = () => {
                if (generation !== this.generation) return;
                try {
                    if (this.video.readyState >= 2 && this.video.videoWidth > 0 && this.video.videoHeight > 0) {
                        const scale = Math.min(1, 1280 / Math.max(this.video.videoWidth, this.video.videoHeight));
                        this.canvas.width = Math.round(this.video.videoWidth * scale);
                        this.canvas.height = Math.round(this.video.videoHeight * scale);
                        context.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
                        const frame = context.getImageData(0, 0, this.canvas.width, this.canvas.height);
                        const code = jsQR(frame.data, frame.width, frame.height, {inversionAttempts: 'attemptBoth'});
                        if (code?.data) { this.stop(); this.onCode(code.data); return; }
                    }
                    this.timer = setTimeout(detect, 200);
                } catch { this.stop(); this.message.textContent = 'カメラ画像を読み取れませんでした。もう一度お試しください。'; }
            };
            detect();
        } catch (error) {
            if (generation !== this.generation) return;
            this.stop();
            const name = error instanceof DOMException ? error.name : '';
            // 記憶していたカメラが今の端末に無い場合は、既定のカメラで開き直す。
            if (requested && retryWithoutDevice && (name === 'OverconstrainedError' || name === 'NotFoundError')) {
                try { localStorage.removeItem(STORAGE_KEY); } catch { /* 記憶できていないだけなので無視する */ }
                await this.start(undefined, false);
                return;
            }
            this.message.textContent = name === 'NotAllowedError' ? 'カメラの利用が許可されていません。ブラウザーのサイト設定で許可してから再度お試しください。'
                : name === 'NotFoundError' ? 'カメラが見つかりません。接続を確認してください。'
                : name === 'NotReadableError' ? 'カメラを起動できません。他のアプリで使用中の場合は閉じてから再度お試しください。'
                : 'カメラを使用できません。接続や権限を確認して再度お試しください。';
        }
    }
}
